'use client';

import { SectionStack } from '@/components/ui/SectionStack';
import React, { useState } from 'react';
import { Hero } from '@/features/b2b/events/components/Hero';
import CustomDropdown from '@/components/ui/CustomDropdown';
import { EventSidebar } from '@/features/b2b/events/components/EventSidebar';
import { TabComponent } from '@/features/b2b/events/components/TabComponents';
import { Footer } from '@/features/b2b/events/components/Footer';
import { PartneredEvent } from '@/features/b2b/events/components/PartneredEvent';
import { EventsTable } from '@/features/b2b/events/components/EventsTable';
import { useEventsData } from '@/features/b2b/events/hooks/useEventsData';
import { useEventsFiltering } from '@/features/b2b/events/hooks/useEventsFiltering';
import { CustomButton } from '@/components/ui/CustomButton';
import { Banner } from '@/components/Banner';
import { ADVISER_PORTAL } from '@/constant/external-links';

export const Events = ({ initialData, eventsBannerData }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const { hasError, partneredEvents, processedTableData } = useEventsData(initialData);
  const {
    selectedEventType,
    columnFilters,
    pagination,
    eventTypes,
    setColumnFilters,
    setPagination,
    handleCriteriaChange,
  } = useEventsFiltering();

  return (
    <SectionStack>
      {/* The banner sits flush on the hero */}
      <div>
        {eventsBannerData?.data?.length > 0 && (
          <Banner banner={eventsBannerData.data[0]} />
        )}
        <Hero />
      </div>
      <SectionStack className="container mx-auto">
        <div className="lg:grid lg:grid-cols-[20rem_36rem] xl:grid-cols-[20rem_55rem] lg:gap-8">
          <div className="pb-[3.25rem] lg:hidden">
            <p id="events-filter-label" className="text-[#000] font-semibold text-xl lg:hidden">Filter</p>
            <CustomDropdown
              aria-labelledby="events-filter-label"
              options={eventTypes}
              value={selectedEventType}
              onChange={handleCriteriaChange}
              isOpen={isDropdownOpen}
              onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
            />
          </div>
          <EventSidebar
            eventTypes={eventTypes}
            selectedEventType={selectedEventType}
            onEventTypeChange={handleCriteriaChange}
          />
          <div>
            <h2 className="text-[#0C0D1A] font-semibold text-3xl md:text-4xl pb-4">Pure Events</h2>
            <p className="text-[#373945] font-450 text-base pb-12">
              These events are specifically designed to expand your knowledge, refine your skills, and provide valuable insights
              into the later life market. By attending our events, you can gain a deeper understanding and practical
              expertise of industry.
            </p>
            <TabComponent onTabChange={(tab) => setActiveTab(tab)} />

            {activeTab === 'upcoming' ? (
              hasError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 text-base">Error loading events: {hasError}</p>
                  <p className="text-[#373945] text-sm mt-2">Please try refreshing the page or check back later.</p>
                </div>
              ) : (
                <>
                  <EventsTable
                    processedTableData={processedTableData}
                    columnFilters={columnFilters}
                    pagination={pagination}
                    onColumnFiltersChange={setColumnFilters}
                    onPaginationChange={setPagination}
                  />
                </>
              )
            ) : (
              <div key="past-events">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h2 className="text-base md:text-lg font-semibold text-[#0C0D1A]">Missed an online event? Watch on demand.</h2>
                    <p className="text-[#373945] font-450 text-base">
                      Log in to your Pure account and watch our events on demand at a time that works best for you.
                      In addition, all our events count towards your CPD hours!
                    </p>
                    <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                      <p className="text-[#373945] font-450 text-base">Don&#39;t have an account with us?</p>
                      <a href="/professionals/register" className="text-[#006EB7] font-550 underline cursor-pointer">Register here &gt;</a>
                    </div>
                    <div>
                      <CustomButton
                        variant="primaryMedium"
                        onClick={() => {window.open(ADVISER_PORTAL.RECORDED_EVENTS, '_blank');}}
                      >
                        Watch back
                      </CustomButton>
                    </div>
                  </div>
                  <div className="mt-6 lg:mt-0">
                    <picture className="block w-full">
                      <source
                        media="(max-width: 767px)"
                        srcSet="/img/adviser/events/past-event.webp"
                      />
                      <img
                        src="/img/adviser/events/past-event.webp"
                        alt="Person watching an online event"
                        className="w-full"
                      />
                    </picture>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Only take a slot in the page stack when there are partner events */}
        {partneredEvents?.length > 0 && (
          <div>
            <PartneredEvent partneredEvents={partneredEvents} />
          </div>
        )}
      </SectionStack>
      <Footer />
    </SectionStack>
  );
};
